import { apiClient } from './apiClient';
import {
  DashboardSummary,
  Income,
  MonthlyReport,
  MonthComparison,
  ProjectDetail,
  ProjectSummary,
  ProjectsOverview,
  Category,
} from '../types';
import { IncomeType, OwnerWithDebt } from '../types';

export const incomesApi = {
  list: (year?: number, month?: number) => {
    const params = new URLSearchParams();
    if (year) params.set('year', String(year));
    if (month) params.set('month', String(month));
    const qs = params.toString();
    return apiClient.get<Income[]>(`/incomes${qs ? `?${qs}` : ''}`);
  },
  create: (data: {
    year: number;
    month: number;
    type: IncomeType;
    amount: number;
    description?: string;
  }) => apiClient.post<Income>('/incomes', data),
  update: (id: number, data: Partial<{
    year: number;
    month: number;
    type: IncomeType;
    amount: number;
    description?: string;
  }>) => apiClient.put<Income>(`/incomes/${id}`, data),
  delete: (id: number) => apiClient.delete(`/incomes/${id}`),
};

export const expensesApi = {
  list: (year?: number, month?: number) => {
    const params = new URLSearchParams();
    if (year) params.set('year', String(year));
    if (month) params.set('month', String(month));
    const qs = params.toString();
    return apiClient.get(`/expenses${qs ? `?${qs}` : ''}`);
  },
  suggestions: () => apiClient.get('/expenses/suggestions'),
  create: (data: Record<string, unknown>) => apiClient.post('/expenses', data),
  update: (id: number, data: Record<string, unknown>) =>
    apiClient.put(`/expenses/${id}`, data),
  delete: (id: number) => apiClient.delete(`/expenses/${id}`),
};

export const reportsApi = {
  dashboard: () => apiClient.get<DashboardSummary>('/reports/dashboard'),
  monthly: (year: number, month: number) =>
    apiClient.get<MonthlyReport>(`/reports/monthly?year=${year}&month=${month}`),
  compare: (months: { year: number; month: number }[]) =>
    apiClient.post<MonthComparison[]>('/reports/compare', { months }),
  yearOverview: (year: number) =>
    apiClient.get(`/reports/year?year=${year}`),
};

export const projectsApi = {
  list: (ownerId?: number) => {
    const qs = ownerId ? `?ownerId=${ownerId}` : '';
    return apiClient.get<ProjectSummary[]>(`/projects${qs}`);
  },
  overview: () => apiClient.get<ProjectsOverview>('/projects/overview'),
  get: (id: number) => apiClient.get<ProjectDetail>(`/projects/${id}`),
  create: (data: {
    name: string;
    description?: string;
    categoryId: number;
    ownerId?: number | null;
    totalAmount: number;
  }) => apiClient.post<ProjectSummary>('/projects', data),
  update: (id: number, data: Record<string, unknown>) =>
    apiClient.put<ProjectSummary>(`/projects/${id}`, data),
  delete: (id: number) => apiClient.delete(`/projects/${id}`),
  addPayment: (id: number, data: { amount: number; paidOn: string; notes?: string }) =>
    apiClient.post(`/projects/${id}/payments`, data),
  deletePayment: (projectId: number, paymentId: number) =>
    apiClient.delete(`/projects/${projectId}/payments/${paymentId}`),
};

export const categoriesApi = {
  list: () => apiClient.get<Category[]>('/categories'),
  create: (data: { name: string; color?: string; icon?: string }) =>
    apiClient.post<Category>('/categories', data),
  update: (id: number, data: { name?: string; color?: string; icon?: string }) =>
    apiClient.put<Category>(`/categories/${id}`, data),
  delete: (id: number) => apiClient.delete(`/categories/${id}`),
};

export const ownersApi = {
  list: (withDebt = false) =>
    apiClient.get<OwnerWithDebt[]>(`/owners${withDebt ? '?withDebt=true' : ''}`),
  create: (data: {
    name: string;
    phone?: string;
    email?: string;
    notes?: string;
  }) => apiClient.post<OwnerWithDebt>('/owners', data),
  update: (
    id: number,
    data: { name?: string; phone?: string; email?: string; notes?: string },
  ) => apiClient.put<OwnerWithDebt>(`/owners/${id}`, data),
  delete: (id: number) => apiClient.delete(`/owners/${id}`),
};
